import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useFeedStore } from '@/stores/feed';
import { useAuthStore } from '@/stores/auth';
import { mediaUrl } from '@/hooks/useMediaUrl';
import { api } from '@/lib/api';
import type { CommentWithAuthor, PostWithAuthor } from '@shared/types';

interface LikerInfo {
  id: string;
  display_name: string;
  username: string;
}

function SharedPostEmbed({ post }: { post: PostWithAuthor }) {
  return (
    <div className="mt-2 border border-sand-300 rounded bg-sand-50 p-3">
      <div className="flex gap-2 mb-1.5">
        <Link to={`/profile/${post.author?.username}`}>
          <img src={mediaUrl(post.author?.avatar_key)} alt="" className="w-7 h-7 rounded object-cover" />
        </Link>
        <div>
          <Link to={`/profile/${post.author?.username}`} className="link font-bold text-xs">
            {post.author?.display_name}
          </Link>
          <div className="text-[10px] text-sand-500">{timeAgo(post.created_at)}</div>
        </div>
      </div>
      <p className="text-xs leading-relaxed whitespace-pre-wrap text-sand-700">{post.content}</p>
      {post.image_key && (
        <div className="mt-1.5">
          <img
            src={mediaUrl(post.image_key)}
            alt=""
            className="w-full max-h-48 object-contain rounded border border-sand-200"
          />
        </div>
      )}
    </div>
  );
}

// Time ago helper (module-level so SharedPostEmbed can use it)
function timeAgo(dateStr: string) {
  const now = new Date();
  const then = new Date(dateStr + 'Z');
  const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return then.toLocaleDateString();
}

export function PostCard({ post }: { post: any }) {
  const { toggleLike, deletePost } = useFeedStore();
  const { user } = useAuthStore();
  const [showComments, setShowComments] = useState(post.comment_count > 0);
  const [comments, setComments] = useState<CommentWithAuthor[]>([]);
  const [commentText, setCommentText] = useState('');
  const [loadedComments, setLoadedComments] = useState(false);
  const [shared, setShared] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [likers, setLikers] = useState<LikerInfo[] | null>(null);
  const likersLoadedRef = useRef(false);

  // Fetch likers when like_count > 0
  useEffect(() => {
    if (post.like_count > 0 && !likersLoadedRef.current) {
      likersLoadedRef.current = true;
      api.get<LikerInfo[]>(`/posts/${post.id}/likers?limit=3`)
        .then(setLikers)
        .catch(() => {});
    }
  }, [post.id, post.like_count]);

  const loadComments = async () => {
    if (loadedComments) return;
    try {
      const data = await api.get<CommentWithAuthor[]>(`/posts/${post.id}/comments`);
      setComments(data);
      setLoadedComments(true);
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    if (showComments && !loadedComments) loadComments();
  }, [showComments]);

  const submitComment = async () => {
    if (!commentText.trim()) return;
    try {
      const newComment = await api.post<CommentWithAuthor>(`/posts/${post.id}/comments`, { content: commentText });
      setComments([...comments, newComment]);
      setCommentText('');
    } catch {
      // ignore
    }
  };

  const handleShare = async () => {
    if (sharing || shared) return;
    setSharing(true);
    try {
      await api.post(`/posts/${post.id}/share`);
      setShared(true);
    } catch {
      // ignore
    }
    setSharing(false);
  };

  const isMe = user?.id === post.author_id;
  const hasWallOwner = post.wall_owner && post.wall_owner.id !== post.author_id;

  const renderLikerNames = () => {
    if (!likers || likers.length === 0 || post.like_count === 0) {
      return post.like_count > 0 ? ` \u00b7 ${post.like_count}` : '';
    }
    const names = likers.map((l) => l.display_name);
    const remaining = post.like_count - names.length;
    if (remaining <= 0) {
      return (
        <span className="text-sand-500 ml-1">
          {names.join(', ')} {names.length === 1 ? 'likes' : 'like'} this
        </span>
      );
    }
    return (
      <span className="text-sand-500 ml-1">
        {names.join(', ')} and {remaining} {remaining === 1 ? 'other' : 'others'} like this
      </span>
    );
  };

  return (
    <div className="card mb-3">
      <div className="p-3">
        <div className="flex gap-2 mb-2">
          <Link to={`/profile/${post.author?.username}`}>
            <img src={mediaUrl(post.author?.avatar_key)} alt="" className="w-10 h-10 rounded object-cover" />
          </Link>
          <div className="flex-1 min-w-0">
            <div className="text-xs">
              <Link to={`/profile/${post.author?.username}`} className="link font-bold">
                {post.author?.display_name}
              </Link>
              {hasWallOwner && (
                <>
                  <span className="text-sand-500 mx-1">&#9658;</span>
                  <Link to={`/profile/${post.wall_owner.username}`} className="link font-bold">
                    {post.wall_owner.display_name}
                  </Link>
                </>
              )}
            </div>
            <div className="text-sand-500 text-[10px]">{timeAgo(post.created_at)}</div>
          </div>
          {isMe && (
            <button onClick={() => deletePost(post.id)} className="ml-auto text-sand-400 hover:text-red-500 text-xs self-start">&#10005;</button>
          )}
        </div>

        <p className="text-sm leading-relaxed whitespace-pre-wrap">{post.content}</p>

        {/* Post image */}
        {post.image_key && (
          <div className="mt-2">
            <img
              src={mediaUrl(post.image_key)}
              alt=""
              className="w-full max-h-96 object-contain rounded border border-sand-200 bg-sand-100"
            />
          </div>
        )}

        {/* Shared/embedded post */}
        {post.shared_post && (
          <SharedPostEmbed post={post.shared_post} />
        )}
      </div>

      {/* Liker names */}
      {post.like_count > 0 && likers && likers.length > 0 && (
        <div className="px-3 pb-1 text-[10px]">
          {renderLikerNames()}
        </div>
      )}

      {/* Actions */}
      <div className="border-t border-sand-300 px-3 py-1.5 flex gap-5 text-xs text-sand-500">
        <button
          onClick={() => toggleLike(post.id)}
          className={`cursor-pointer transition-colors ${post.liked_by_me ? 'text-amber-400 font-bold' : 'hover:text-sand-700'}`}
        >
          {post.liked_by_me ? '\u2605' : '\u2606'} {post.liked_by_me ? 'Liked' : 'Like'}
          {post.like_count > 0 && !likers && ` \u00b7 ${post.like_count}`}
        </button>
        <button
          onClick={() => setShowComments(true)}
          className="cursor-pointer hover:text-sand-700"
        >
          &#128172; Comment{post.comment_count > 0 && ` \u00b7 ${post.comment_count}`}
        </button>
        <button
          onClick={handleShare}
          disabled={sharing}
          className={`cursor-pointer transition-colors ${shared ? 'text-vibe-500 font-bold' : 'hover:text-sand-700'}`}
        >
          {shared ? '&#10003; Shared!' : sharing ? 'Sharing...' : '&#8634; Share'}
          {post.share_count > 0 && ` \u00b7 ${post.share_count}`}
        </button>
      </div>

      {/* Comments */}
      {showComments && (
        <div className="border-t border-sand-300 bg-sand-100 px-3 py-2">
          {comments.map((c: any) => (
            <div key={c.id} className="flex gap-1.5 mb-2">
              <Link to={`/profile/${c.author_username || c.author?.username}`}>
                <img src={mediaUrl(c.author_avatar_key || c.author?.avatar_key)} alt="" className="w-6 h-6 rounded object-cover shrink-0" />
              </Link>
              <div>
                <Link to={`/profile/${c.author_username || c.author?.username}`} className="link font-bold text-xs">
                  {c.author_display_name || c.author?.display_name}
                </Link>{' '}
                <span className="text-xs">{c.content}</span>
                <div className="text-[10px] text-sand-500 mt-0.5">{timeAgo(c.created_at)}</div>
              </div>
            </div>
          ))}
          <div className="flex gap-1.5 mt-1">
            <img src={mediaUrl(user?.avatar_key)} alt="" className="w-6 h-6 rounded object-cover shrink-0" />
            <input
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submitComment(); }}
              placeholder="Drop a comment..."
              className="input flex-1 text-xs py-1"
            />
          </div>
        </div>
      )}
    </div>
  );
}
